import { Card, Divider } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { formatCurrency } from '../../../../../utils/billingUtils';

/**
 * PaymentSummaryCard - Premium display of billing breakdown and payment records
 */
const PaymentSummaryCard = ({ order, billing }) => {
    const totalAmount = billing?.total || 0;
    const discount = billing?.discountValue || 0;
    const finalAmount = billing?.netAmount || 0;
    const paidAmount = order?.updatedPaidAmount || billing?.paid || 0;
    const pendingAmount = billing?.pending || 0;

    const paymentPercent = billing?.isFullyPaid ? 100 :
        (finalAmount > 0 ? Math.min(100, Math.round((Number(paidAmount) / finalAmount) * 100)) : 100);

    return (
        <Card
            className="mb-3 shadow-sm"
            style={{
                position: 'sticky',
                top: '20px',
                borderRadius: '16px',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                background: '#ffffff'
            }}
            bodyStyle={{ padding: '24px' }}
            bordered={false}
        >
            {/* Header */}
            <div className="mb-4 d-flex justify-content-between align-items-center">
                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#111827', letterSpacing: '-0.025em' }}>
                    Payment Summary
                </h4>
                <div style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: paymentPercent === 100 ? '#f0fdf4' : '#eff6ff',
                    color: paymentPercent === 100 ? '#16a34a' : '#2563eb',
                    border: `1px solid ${paymentPercent === 100 ? '#bbf7d0' : '#bfdbfe'}`
                }}>
                    {paymentPercent === 100 ? 'FULLY PAID' : `${paymentPercent}% COLLECTED`}
                </div>
            </div>

            {/* Calculations Breakdown */}
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Subtotal</span>
                    <span style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>{formatCurrency(totalAmount)}</span>
                </div>

                {discount > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Discount Applied</span>
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#10b981' }}>- {formatCurrency(discount)}</span>
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Total Net Amount</span>
                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{formatCurrency(finalAmount)}</span>
                </div>

                <Divider style={{ margin: '16px 0', borderStyle: 'dashed' }} />

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: '14px', color: '#2563eb', fontWeight: '600' }}>Total Paid (to date)</span>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: '#2563eb' }}>{formatCurrency(paidAmount)}</span>
                </div>

                {!billing?.isFullyPaid && pendingAmount > 0 && (
                    <div className="d-flex justify-content-between align-items-center p-3 rounded-3 mt-3" style={{ background: '#fff1f2', border: '1px solid #ffe4e6' }}>
                        <span style={{ fontSize: '13px', color: '#be123c', fontWeight: '700' }}>BALANCE DUE</span>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: '#be123c' }}>{formatCurrency(pendingAmount)}</span>
                    </div>
                )}
            </div>

            {/* Stakeholder Details */}
            <div className="pt-4" style={{ borderTop: '1px solid #f3f4f6' }}>
                <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', fontWeight: '800', marginBottom: '16px' }}>
                    Payment Administration
                </p>

                <div className="d-flex align-items-start mb-3">
                    <div style={{ background: '#f9fafb', padding: '10px', borderRadius: '12px', marginRight: '12px' }}>
                        <FeatherIcon icon="user" size={16} color="#4b5563" />
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600' }}>CREATED BY</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                            {order?.createdByName || 'N/A'}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-start mb-3">
                    <div style={{ background: '#f9fafb', padding: '10px', borderRadius: '12px', marginRight: '12px' }}>
                        <FeatherIcon icon="edit-3" size={16} color="#4b5563" />
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600' }}>COLLECTED BY</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                            {order?.updatedByName || 'N/A'}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-start">
                    <div style={{ background: '#f9fafb', padding: '10px', borderRadius: '12px', marginRight: '12px' }}>
                        <FeatherIcon icon="credit-card" size={16} color="#4b5563" />
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600' }}>PAYMENT METHOD</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>
                            {order?.paymentMode || 'N/A'}
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default PaymentSummaryCard;
